import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, List, Spin, Button } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import ViewRewardModal from '../../ViewRewardModal/ViewRewardModal.jsx';
import CoreBadge from 'src/RnRCommonComponent/CoreBadge/CoreBadge';
import { showBadgeNumber } from './helper.js';
import { addCommas } from '../../../rewardsAndRecognitionHelper/utils.js';

function useWindowSize() {
	const [width, setWidth] = useState(window.innerWidth);

	useEffect(() => {
		function handleResize() {
			setWidth(window.innerWidth);
		}

		window.addEventListener('resize', handleResize);
		handleResize();

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return width;
}

const RewardList = (props) => {
	const {
		handleIconClickEvent,
		title,
		badgeList,
		badgeType,
		hasMore,
		isLoading,
		fetchMoreData,
		isDetailsShown,
		handleOpenViewReward,
		handleCloseViewReward,
		isViewRewardModalVisible,
		rewardDetails,
		isModalLoaderVisible,
		getMyBadge,
		isPointsShown,
		isIssuedVisible,
		currentUser,
		customHeight,
	} = props;
	const width = useWindowSize();
	const scrollContainerRef = useRef(null);

	const [badgeExpanded, setBadgeExpanded] = useState(false);

	const handleScroll = useCallback(() => {
		if (!scrollContainerRef.current || !hasMore || isLoading) return;
		
		const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
		const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
		
		if (scrollPercentage > 0.9) {
			fetchMoreData();
		}
	}, [hasMore, isLoading, fetchMoreData]);

	useEffect(() => {
		const scrollContainer = scrollContainerRef.current;
		if (scrollContainer) {
			scrollContainer.addEventListener('scroll', handleScroll);
			return () => scrollContainer.removeEventListener('scroll', handleScroll);
		}
	}, [handleScroll]);

	const visibleBadges = badgeExpanded
		? badgeList
		: badgeList?.slice(0, showBadgeNumber(width, badgeType));

	const handleOnClick = (badge) => {
		if (handleIconClickEvent) {
			handleIconClickEvent(badge);
		}
	};
	const badgesData =
		visibleBadges?.map((badge, index) => {
			const hasSnappyId = badge?.snappyGrantId || badge?.snappyGrant?.id;
			const isManagerOrAdmin = ['manager', 'admin', 'superadmin'].includes(currentUser?.role?.toLowerCase());
			const hideSnappyIcon = !(hasSnappyId && isManagerOrAdmin);
			const currencySymbol = badge?.snappyGrant?.currencySymbol || '$';
			
			const snappyDetails = hasSnappyId ? {
				snappyGrantId: badge?.snappyGrantId || badge?.snappyGrant?.id,
				snappyGrantName: badge?.snappyGrant?.name || 'Snappy Reward',
				costInCurrency: `${currencySymbol}${badge?.snappyGrant?.costInCurrency || '0'}`,
				description: badge?.snappyGrant?.description || 'Snappy Gift Attached ⭐️'
			} : {};

			return (
				<>
				<div
					key={index}
					className="badges-container"
					disabled={badge.visibility === false}
					style={{
						opacity:
							(badge.visibility !== false) || getMyBadge
								? 1
								: 0.3,
					}}
				>
					<div
						className="badges-container-edit-target"
						style={{ cursor: 'pointer' }}
					>
						<div
							className="badges-img"
							onClick={() => {
								handleOnClick(badge);
								!handleIconClickEvent &&
								!isDetailsShown &&
								handleOpenViewReward(
									badge.id
								);
							}}
						>
							{
								<div className="badges-svg">
									<CoreBadge
										badgeType={
											badge?.badgeType
										}
										badgeIcon={
											badge?.iconInfo?.code
										}
										label={badge?.label}
										color={badge?.color}
										gradientColor={badge?.gradientColor}
										hideSnappyIcon={hideSnappyIcon}
										snappyDetails={snappyDetails}
									/>
								</div>
							}
							<div className="text-center img-label">
								{isPointsShown ? (
									addCommas(
										badge.points
									) + ' Points'
								) : isIssuedVisible ? (
									<div
										style={{
											display: 'flex',
											flexDirection:
												'column',
											alignItems:
												'center',
											fontSize: '11px',
										}}
									>
										<div>
											{badge.completedAt || badge.issuedDate || badge.createdAt ?
												new Date(badge.completedAt || badge.issuedDate || badge.createdAt).toLocaleDateString() :
												'Earned'
											}
										</div>
									</div>
								) : (
									badge.name
								)}
							</div>
						</div>
					</div>
			{isDetailsShown ? (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						marginTop: '2px',
					}}
				>
					<Button
						type="link"
						onClick={() => {
							handleOpenViewReward(
								badge.id,
								badge.completedAt || badge.issuedDate
							);
						}}
						className="text-center img-label"
					>
						Details
					</Button>
				</div>
			) : null}
				</div>
				</>
			)
		})
		;

	return (
		<div className="d-card ">
			<div className="d-card-head">
				<h4 className="d-title-no-case">{title}</h4>
			</div>
			<div
				id={badgeType}
				ref={scrollContainerRef}
				style={{
					maxHeight: customHeight || (badgeExpanded ? '500px' : isDetailsShown ? '190px' : '170px'),
					height: '100%',
					overflowY: 'scroll',
					WebkitOverflowScrolling: 'touch'
				}}
			>
			<List
				grid={{
					gutter: 8,
					xs: 1,
					sm: 2,
					md: 3,
					lg: 3,
					xl: 4,
					xxl: 6,
				}}
				dataSource={badgesData}
				renderItem={(item) => (
					<List.Item>
						<Card className="badge-card" style={{textAlign: 'center', padding: 0, border: 'none'}}>{item}</Card>
					</List.Item>
				)}
			/>
			</div>
			{
				<div className="d-flex justify-content-center align-items-center  m-2">
					{visibleBadges?.length && isLoading === true ? (
						<Spin className="spinner" />
					) : (
						<div
							// disabled
							onClick={() => {
								setBadgeExpanded(!badgeExpanded);
								if (!badgeExpanded && hasMore) {
									fetchMoreData();
								}
							}}
							className="d-flex justify-content-center align-items-center collpse-btn"
						>
							{badgeExpanded ? (
								<>
									Collapse <UpOutlined className="ml-1" />
								</>
							) : visibleBadges?.length >= badgeList?.length ? (
								<></>
							) : (
								<>
									Expand <DownOutlined className="ml-1" />
								</>
							)}
						</div>
					)}
				</div>
			}
			<ViewRewardModal
				isViewRewardModalVisible={isViewRewardModalVisible}
				handleCloseViewReward={handleCloseViewReward}
				rewardDetails={rewardDetails}
				isModalLoaderVisible={isModalLoaderVisible}
				isSharingVisible={false}
				currentUser={currentUser}
			/>
		</div>
	);
};

export default RewardList;
